import React, { useState, useEffect } from "react";
import { TextField, Button, Typography, Container, Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { markAttendance, getGymDetails } from "./api";
import {
  getMobileNumber,
  saveMobileNumber,
  getCheckInMessage,
  saveCheckInMessage,
} from "./storage";

const firebaseConfig = {
  apiKey: "AIzaSyDSiNAB4FJ9yY8Gu2WDL7ZHYPcyHL1CuFc",
  authDomain: "gymbook-member-prod-web-app.firebaseapp.com",
  projectId: "gymbook-member-prod-web-app",
  storageBucket: "gymbook-member-prod-web-app.firebasestorage.app",
  messagingSenderId: "945246217509",
  appId: "1:945246217509:web:dddebcb525c2f81e6be6cc",
  measurementId: "G-SCMQSSF285",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

function App() {
  const [mobileNumber, setMobileNumber] = useState(getMobileNumber);
  const [subId, setSubId] = useState("");
  const [attendanceMessage, setAttendanceMessage] = useState(getCheckInMessage);
  const [gymDetail, setGymDetail] = useState({
    _id: null,
    subLogo: null,
    subName: null,
  });
  const [gymIdMissing, setGymIdMissing] = useState(false);
  const [messageType, setMessageType] = useState("success");

  const setMessageForLocal = (message, type = "success") => {
    setAttendanceMessage(message);
    setMessageType(type);
    saveCheckInMessage(message);
  };

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gymId = params.get("subID");
    if (analytics) {
      logEvent(analytics, "page_view", { page_path: location.pathname });
    }

    if (gymId) {
      setSubId(gymId);
      setGymIdMissing(false);
      getGymDetailFromServer(gymId);
    } else {
      setGymIdMissing(true);
    }
  }, [location]);

  const handleInputChange = (event) => {
    setMobileNumber(event.target.value);
  };

  const handleAttendance = async () => {
    const trimmedMobileNumber = mobileNumber.trim();

    if (trimmedMobileNumber === "") {
      setMessageForLocal("Please enter a valid mobile number.");
      return;
    }
    try {
      setMobileNumber(trimmedMobileNumber);

      const response = await markAttendance(trimmedMobileNumber, subId);
      saveMobileNumber(trimmedMobileNumber);

      // Get current date & time
      const now = new Date();
      const day = String(now.getDate()).padStart(2, "0");
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[now.getMonth()];
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");

      const formattedDateTime = `on ${day}-${month}-${year} at ${hours}:${minutes}`;

      const displayMessage = `${response.message} ${formattedDateTime}`;
      const type = displayMessage.toLowerCase().includes("expired")
        ? "expired"
        : "success";
      setMessageForLocal(displayMessage, type);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Failed to mark attendance. Please try again.";
      setMessageForLocal(msg, "error");
    }
  };

  const getGymDetailFromServer = async (subID) => {
    const gymDetail = await getGymDetails(subID);
    setGymDetail(gymDetail);
  };

  if (gymIdMissing) {
    return (
      <Container
        maxWidth="sm"
        style={{
          marginTop: "50px",
          backgroundColor: "#ffffff", // White card background
          padding: "30px",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0, 96, 100, 0.2)",
          position: "relative",
        }}
      >
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            style={{ fontWeight: "bold", color: "#D32F2F" }}
          >
            Gym Not Found
          </Typography>
          <Typography
            variant="body1"
            gutterBottom
            style={{ color: "#006064", fontSize: "18px", marginTop: "20px" }}
          >
            Please scan QR again. Gym not found.
          </Typography>
        </Box>

        <Box
          mt={5}
          textAlign="right"
          style={{
            color: "#004D40",
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          <Typography variant="body2">
            Powered by <strong style={{ color: "#006064" }}>GymBook</strong>
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="sm"
      style={{
        marginTop: "50px",
        backgroundColor: "#ffffff", // White card background
        padding: "30px",
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0, 96, 100, 0.2)",
        position: "relative",
      }}
    >
      <Box textAlign="center" mb={4}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          style={{ fontWeight: "bold", color: "#004D40" }}
        >
          Welcome to {gymDetail.subName || "Gym"}
        </Typography>
        <Typography variant="body1" gutterBottom style={{ color: "#006064" }}>
          {"Enter your mobile number to mark attendance."}
        </Typography>
      </Box>

      <Box mb={2}>
        <TextField
          fullWidth
          label="Mobile Number"
          variant="outlined"
          value={mobileNumber}
          onChange={handleInputChange}
        />
      </Box>

      <Box textAlign="center">
        <Button
          variant="contained"
          style={{
            backgroundColor: "#006064",
            color: "#ffffff",
            padding: "12px 24px",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "bold",
          }}
          onClick={handleAttendance}
        >
          Mark Attendance
        </Button>
      </Box>

      {attendanceMessage && (
        <Box mt={3} textAlign="center">
          <Typography
            variant="body1"
            style={{
              color:
                messageType === "error"
                  ? "#D32F2F"
                  : messageType === "expired"
                  ? "#F57C00"
                  : "#388E3C",
              fontWeight: "bold",
            }}
          >
            {attendanceMessage}
          </Typography>
        </Box>
      )}

      <Box
        mt={5}
        textAlign="right"
        style={{
          color: "#004D40",
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        <Typography variant="body2">
          Powered by <strong style={{ color: "#006064" }}>GymBook</strong>
        </Typography>
      </Box>
    </Container>
  );
}

export default App;
